import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg, fmtDate, inr, Money } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { DataTable, DemoBadge, EntityCombobox, Field, PageHeader, SearchBox, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Quotations() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const empty = { customer_id: "", travel_start: "", travel_end: "", destination: "", people: 2,
    items: [], notes: "", status: "Draft" };
  const [form, setForm] = useState(empty);
  const writable = can(user?.role, "quotations", true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/quotations", { params: { q, page, limit: 25 } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [q, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/customers?limit=200").then((r) => setCustomers(r.data.items)).catch(() => {});
    api.get("/services?active_only=true&limit=200").then((r) => setServices(r.data.items)).catch(() => {});
    if (params.get("new") === "1" && writable) { setForm(empty); setOpen(true); }
    const focus = params.get("focus");
    if (focus) api.get(`/quotations/${focus}`).then((r) => { setForm({ ...empty, ...r.data }); setOpen(true); }).catch(() => {});
  }, []);

  const addItem = (svc) => setForm((f) => ({ ...f, items: [...f.items, svc
    ? { description: svc.name, sac: svc.sac, qty: 1, unit: svc.unit, rate: svc.price, discount: 0, gst_rate: svc.gst_rate }
    : { description: "", sac: "", qty: 1, unit: "Nos", rate: 0, discount: 0, gst_rate: 5 }] }));
  const setItem = (i, patch) => setForm((f) => {
    const items = [...f.items];
    items[i] = { ...items[i], ...patch };
    return { ...f, items };
  });

  const submit = async () => {
    if (!form.customer_id) return toast.error("Select a customer");
    if (!form.items.length) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const isEdit = !!form.id;
      const r = isEdit ? await api.put(`/quotations/${form.id}`, form) : await api.post("/quotations", form);
      (r.data.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Quotation ${r.data.item.quotation_no} saved`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const convertToBooking = async (row) => {
    try {
      const r = await api.post(`/quotations/${row.id}/convert-to-booking`);
      toast.success(`Converted to booking ${r.data.booking_id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const total = form.items.reduce((s, it) => s + (it.qty || 1) * (it.rate || 0) - (it.discount || 0), 0);

  return (
    <div>
      <PageHeader title="Quotations" subtitle="Professional travel quotations — convert to booking or invoice"
        testid="quotations-header"
        actions={
          <>
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search quotation, customer…" testid="quotations-search" />
            {writable && (
              <Button className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="quotations-create-button"
                onClick={() => { setForm(empty); setOpen(true); }}>
                <Plus className="h-4 w-4 mr-1" /> New Quotation
              </Button>
            )}
          </>
        } />
      <DataTable testid="quotations-table" loading={loading}
        columns={[
          { key: "quotation_no", label: "Number", render: (r) => <span className="font-semibold text-[#0D4F4A]">{r.quotation_no}{r.is_demo && <DemoBadge />}</span> },
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "customer_name", label: "Customer" },
          { key: "destination", label: "Destination" },
          { key: "travel", label: "Travel", render: (r) => <span className="text-xs">{fmtDate(r.travel_start)} → {fmtDate(r.travel_end)}</span> },
          { key: "people", label: "Pax", align: "right" },
          { key: "grand_total", label: "Total", align: "right", render: (r) => <Money value={r.grand_total} className="font-semibold" /> },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          { key: "_a", label: "Actions", align: "right", render: (r) => (
            <div className="flex justify-end gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="outline" onClick={() => download(`/quotations/${r.id}/pdf`, "quotation.pdf")}
                data-testid={`quotation-pdf-${r.id}`}>PDF</Button>
              {writable && r.status !== "Converted" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => convertToBooking(r)}
                    data-testid={`quotation-convert-booking-${r.id}`}>→ Booking</Button>
                  {can(user?.role, "invoices", true) && (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/invoices/new?quotation=${r.id}`)}
                      data-testid={`quotation-convert-invoice-${r.id}`}>→ Invoice</Button>
                  )}
                </>
              )}
            </div>) },
        ]}
        rows={data.items} page={data.page} pages={data.pages} total={data.total} onPage={setPage}
        onRow={writable ? (r) => { setForm({ ...empty, ...r }); setOpen(true); } : undefined}
        empty="No quotations yet." />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quotation-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{form.id ? `Edit ${form.quotation_no}` : "New Quotation"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="col-span-2"><Field label="Customer" required>
              <EntityCombobox testid="quotation-customer" value={form.customer_id} onChange={(v) => setForm({ ...form, customer_id: v })}
                items={customers.map((c) => ({ value: c.id, label: c.name, sub: c.phone }))} placeholder="Select customer…" /></Field></div>
            <Field label="Travellers"><Input type="number" min="1" value={form.people} className="bg-white border-[#E5E0D8]"
              onChange={(e) => setForm({ ...form, people: Number(e.target.value) })} data-testid="quotation-people" /></Field>
            <Field label="Destination"><Input value={form.destination} className="bg-white border-[#E5E0D8]"
              onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="quotation-destination" /></Field>
            <Field label="Travel Start"><Input type="date" value={form.travel_start} className="bg-white border-[#E5E0D8]"
              onChange={(e) => setForm({ ...form, travel_start: e.target.value })} data-testid="quotation-start" /></Field>
            <Field label="Travel End"><Input type="date" value={form.travel_end} className="bg-white border-[#E5E0D8]"
              onChange={(e) => setForm({ ...form, travel_end: e.target.value })} data-testid="quotation-end" /></Field>
          </div>
          <div className="flex items-center justify-between mt-4 mb-2">
            <div className="text-xs font-bold uppercase tracking-wide text-[#5C6B69]">Items</div>
            <div className="flex gap-2">
              <EntityCombobox testid="quotation-add-service" value="" onChange={(v) => v && addItem(services.find((s) => s.id === v))}
                items={services.map((s) => ({ value: s.id, label: s.name, sub: `${s.sac} · ${inr(s.price)}` }))} placeholder="+ From catalog…" />
              <Button size="sm" variant="outline" onClick={() => addItem(null)} data-testid="quotation-add-custom">+ Custom</Button>
            </div>
          </div>
          <div className="space-y-2">
            {form.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center border border-[#E5E0D8] rounded-lg p-2 bg-[#FAF8F5]/50" data-testid={`quotation-item-${i}`}>
                <Input className="col-span-5 bg-white border-[#E5E0D8] h-9" value={it.description} placeholder="Description"
                  onChange={(e) => setItem(i, { description: e.target.value })} data-testid={`quotation-item-${i}-desc`} />
                <Input className="col-span-2 bg-white border-[#E5E0D8] h-9" value={it.sac} placeholder="SAC"
                  onChange={(e) => setItem(i, { sac: e.target.value })} />
                <Input className="col-span-1 bg-white border-[#E5E0D8] h-9" type="number" value={it.qty}
                  onChange={(e) => setItem(i, { qty: Number(e.target.value) })} />
                <Input className="col-span-2 bg-white border-[#E5E0D8] h-9" type="number" value={it.rate} placeholder="Rate"
                  onChange={(e) => setItem(i, { rate: Number(e.target.value) })} data-testid={`quotation-item-${i}-rate`} />
                <Select value={String(it.gst_rate)} onValueChange={(v) => setItem(i, { gst_rate: Number(v) })}>
                  <SelectTrigger className="col-span-1 bg-white border-[#E5E0D8] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{[0, 5, 12, 18, 28].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                </Select>
                <Button size="icon" variant="ghost" className="col-span-1 h-9 w-9 text-red-500"
                  onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, x) => x !== i) }))}>×</Button>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center mt-3">
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="w-36 bg-white border-[#E5E0D8]" data-testid="quotation-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["Draft", "Sent", "Accepted", "Rejected"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="text-sm">Taxable (est.): <b className="num text-[#0D4F4A]">{inr(total)}</b> <span className="text-xs text-[#5C6B69]">+ GST on save</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="quotation-submit">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save Quotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
